import {
	type Dtos,
	ExtractionErrorType,
	ExtractionStatus,
	type UserId,
	WipExtractionReviewStatus,
	type WipSchedule,
} from "@inrev/common";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import type { DateTime } from "luxon";
import { type FC, useId, useMemo } from "react";
import { match } from "ts-pattern";
import { formatName } from "../../../utils/string";
import { cn } from "../../lib/utils";
import { Icon, Text } from "../../primitives";
import { PortaledTooltip } from "../../ui/Tooltip";
import { extractionErrorTypeLabels } from "../../uploaded-documents/ExtractionStatusIcon";
import {
	resolveWipReviewDisplayState,
	WIP_REVIEW_TIMESTAMP_FORMAT,
	wipReviewDisplayLabel,
	wipReviewStatusIconClass,
	wipReviewStatusIconType,
} from "./utils/wipReviewStatus";

const EMPTY_ADMIN_USERS: Dtos.Admin.AdminUser.Get.Response[] = [];
const EMPTY_SIGNALS: WipSchedule.WipConfidenceSignal[] = [];

const needsReviewConfidenceReason: Record<WipSchedule.WipConfidenceTier, string> = {
	PARTIAL: "Partial confidence",
	UNCERTAIN: "Uncertain confidence",
	VERIFIED: "Extraction requires underwriter review",
};

export type WipReviewStatusIconProps = {
	reviewStatus: WipExtractionReviewStatus;
	hasCorrections: boolean;
	extractionStatus?: ExtractionStatus;
	errorType?: ExtractionErrorType;
	errorMessage?: string;
	extractionTriggeredAt?: DateTime;
	extractionCompletedAt?: DateTime;
	underwriterReviewedAt?: DateTime;
	underwriterReviewedByUserId?: UserId;
	extractionConfidence?: WipSchedule.WipConfidenceTier;
	signals?: WipSchedule.WipConfidenceSignal[];
};

function buildNeedsReviewReason(
	extractionStatus: ExtractionStatus | undefined,
	extractionConfidence: WipSchedule.WipConfidenceTier | undefined,
): string {
	if (extractionStatus === ExtractionStatus.Partial) {
		return "Extraction partially completed with issues.";
	}
	if (extractionConfidence) {
		return needsReviewConfidenceReason[extractionConfidence];
	}
	return "Extraction requires underwriter review.";
}

function buildNeedsReviewTooltip(
	reason: string,
	signals: readonly WipSchedule.WipConfidenceSignal[],
): string {
	if (signals.length === 0) {
		return reason;
	}
	const signalLines = signals.map((signal) => signal.code.replace(/_/g, " ")).join("\n");
	return `${reason}\n${signalLines}`;
}

export const WipReviewStatusIcon: FC<WipReviewStatusIconProps> = ({
	reviewStatus,
	hasCorrections,
	extractionStatus,
	errorType,
	errorMessage,
	extractionTriggeredAt,
	extractionCompletedAt,
	underwriterReviewedAt,
	underwriterReviewedByUserId,
	extractionConfidence,
	signals,
}) => {
	const signalList = signals ?? EMPTY_SIGNALS;
	const tooltipId = useId();
	const { adminUsers = EMPTY_ADMIN_USERS } = useAdminFetchAdminUsers();
	const displayState = resolveWipReviewDisplayState(reviewStatus, hasCorrections);
	const label = wipReviewDisplayLabel(reviewStatus, hasCorrections);

	const reviewerName = useMemo(() => {
		if (!underwriterReviewedByUserId) {
			return "Underwriter";
		}
		return (
			formatName(adminUsers.find((user) => user.id === underwriterReviewedByUserId)) ??
			"Underwriter"
		);
	}, [adminUsers, underwriterReviewedByUserId]);

	const tooltip = useMemo(
		() =>
			match(displayState)
				.with("failed", () => {
					const baseLabel = extractionErrorTypeLabels[errorType ?? ExtractionErrorType.UNKNOWN];
					const errorLabel = errorMessage ? `${baseLabel} - ${errorMessage}` : baseLabel;
					const timeStamp = extractionCompletedAt?.toFormat(WIP_REVIEW_TIMESTAMP_FORMAT);
					return timeStamp ? `Extraction Failed at ${timeStamp}: ${errorLabel}` : errorLabel;
				})
				.with("reviewed", () => {
					const timeStamp = underwriterReviewedAt?.toFormat(WIP_REVIEW_TIMESTAMP_FORMAT);
					return timeStamp ? `${reviewerName} at ${timeStamp}` : reviewerName;
				})
				.with("needs-review", () =>
					buildNeedsReviewTooltip(
						buildNeedsReviewReason(extractionStatus, extractionConfidence),
						signalList,
					),
				)
				.with("high-confidence", () => "High confidence extraction available in Analysis.")
				.with("in-review", () => "Corrections in progress.")
				.with("pending", () => {
					const timeStamp = extractionTriggeredAt?.toFormat(WIP_REVIEW_TIMESTAMP_FORMAT);
					return timeStamp
						? `Extraction started at ${timeStamp}`
						: "Extraction is currently in progress.";
				})
				.with("not-started", () => "Extraction has not started yet.")
				.exhaustive(),
		[
			displayState,
			errorMessage,
			errorType,
			extractionCompletedAt,
			extractionConfidence,
			extractionStatus,
			extractionTriggeredAt,
			reviewerName,
			signalList,
			underwriterReviewedAt,
		],
	);

	const iconBoxClass = cn(
		"inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border",
		wipReviewStatusIconClass(displayState),
	);

	const iconType = wipReviewStatusIconType(displayState);

	return (
		<span className="inline-flex min-w-0 items-center gap-2" data-testid="wip-review-status">
			<span
				className={iconBoxClass}
				data-testid="wip-review-status-icon"
				data-tooltip-id={tooltipId}
				data-tooltip-content={tooltip}
			>
				<Icon type={iconType} size="mini" />
			</span>
			<Text type="body" color="secondary" data-testid="wip-review-status-label">
				{label}
			</Text>
			<PortaledTooltip id={tooltipId} className="amp-mask sentry-mask" />
		</span>
	);
};
